import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import React, { Suspense, lazy, useEffect, useRef } from 'react';
import Layout from './components/Layout';

const Home = lazy(() => import('./components/Pages/Home'));
const Products = lazy(() => import('./components/Pages/Products'));
const About = lazy(() => import('./components/Pages/About'));
const Careers = lazy(() => import('./components/Pages/Careers'));
const Brochure = lazy(() => import('./components/Pages/Brochure'));
const Privacy = lazy(() => import('./components/Pages/Privacy'));
const Terms = lazy(() => import('./components/Pages/Terms'));
const ModulePage = lazy(() => import('./components/Pages/ModulePage'));
const ModuleSlugRedirect = lazy(() =>
  import('./components/Pages/ModulePage').then((m) => ({
    default: m.ModuleSlugRedirect,
  }))
);
const Appointments = lazy(() => import('./components/Pages/Appointments'));
const DemoSuccess = lazy(() => import('./components/Pages/DemoSuccess'));
const Contact = lazy(() => import('./components/Pages/Contact'));
const ErrorPage = lazy(() => import('./components/Pages/ErrorPage'));

function RouteFallback() {
  return <div className="cs_route_fallback" aria-hidden="true" />;
}

function App() {
  const { pathname, hash } = useLocation();
  const prevPathnameRef = useRef(pathname);

  useEffect(() => {
    const pathChanged = prevPathnameRef.current !== pathname;
    prevPathnameRef.current = pathname;
    // Only reset scroll on real page changes. Hash-only updates (Products /
    // Modules sticky nav) must not jump the page to the top.
    if (pathChanged && !hash) {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="products" element={<Products />} />
          <Route path="about" element={<About />} />
          <Route path="careers" element={<Careers />} />
          <Route path="brochure" element={<Brochure />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<Terms />} />
          <Route path="modules" element={<ModulePage />} />
          <Route path="modules/:moduleSlug" element={<ModuleSlugRedirect />} />
          <Route path="appointments" element={<Appointments />} />
          <Route path="contact" element={<Contact />} />

          {/* Legacy product routes → single Products page hashes */}
          <Route
            path="hospital_HMS"
            element={<Navigate to="/products#hms" replace />}
          />
          <Route
            path="laboratory_LMS"
            element={<Navigate to="/products#lis" replace />}
          />
          <Route path="lis" element={<Navigate to="/products#lis" replace />} />
          <Route
            path="pharmacy_PMS"
            element={<Navigate to="/products#pharmacy" replace />}
          />
          <Route
            path="hrsolutions_MS"
            element={<Navigate to="/products#hr" replace />}
          />
          <Route
            path="finance_MS"
            element={<Navigate to="/products#finance" replace />}
          />
          <Route
            path="departments"
            element={<Navigate to="/products#machine-integration" replace />}
          />
          <Route
            path="ecommerce_MS"
            element={<Navigate to="/products#cloud-services" replace />}
          />
          <Route
            path="clinic_HMS"
            element={<Navigate to="/products" replace />}
          />
          <Route
            path="clinicSolutions"
            element={<Navigate to="/products" replace />}
          />

          {/* Leftover template pages from the original theme */}
          <Route path="doctors" element={<Navigate to="/" replace />} />
          <Route path="doctors/:doctorId" element={<Navigate to="/" replace />} />
          <Route path="blog" element={<Navigate to="/" replace />} />
          <Route path="blog/:blogId" element={<Navigate to="/" replace />} />
          <Route path="gallery" element={<Navigate to="/" replace />} />
          <Route path="timetable" element={<Navigate to="/" replace />} />
          <Route path="pricing-plan" element={<Navigate to="/" replace />} />
          <Route path="homeMain" element={<Navigate to="/" replace />} />
          <Route
            path="departments/:departmentId"
            element={<Navigate to="/products" replace />}
          />
          <Route path="*" element={<ErrorPage />} />
        </Route>
        <Route path="demo-success" element={<DemoSuccess />} />
        <Route path="home-v6" element={<Navigate to="/" replace />} />
        <Route path="home-v3" element={<Navigate to="/" replace />} />
        <Route path="home-v4" element={<Navigate to="/" replace />} />
        <Route path="home-v5" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
